import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowRight, BadgeAlert } from 'lucide-react'
import { env } from '@/lib/env'
import { endOfDayParam, formatDateTimeCompact, startOfDayParam } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { formatMinorCurrency, humanizeEnum, truncate } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import {
  REFUND_SOURCES,
  REFUND_STATUSES,
  type AdminRefund,
  type RefundFilters,
} from '@/types/api'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterDate, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fetchRefundAdmins, fetchRefunds, fetchRefundSummary } from './api'
import { RefundDetailsDrawer } from './RefundDetailsDrawer'
import { RefundSummaryCards } from './RefundSummaryCards'
import styles from './RefundsPage.module.css'

const DEFAULT_FILTERS = {
  search: '',
  status: '',
  source: '',
  adminUserId: '',
  from: '',
  to: '',
}

/**
 * The refund ledger.
 *
 * This is the database, rendered — not a view over browser state. Every row is
 * a permanent record of an attempt to move money, including the ones that
 * failed and the ones whose outcome is still unknown; nothing here can be
 * edited or deleted from the interface.
 */
export function RefundsPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [selected, setSelected] = useState<AdminRefund | null>(null)

  useEffect(() => {
    document.title = `Refunds · ${env.appName} Operations`
  }, [])

  const filters = useMemo<RefundFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      status: table.filters.status || undefined,
      source: table.filters.source || undefined,
      adminUserId: table.filters.adminUserId ? Number(table.filters.adminUserId) : undefined,
      from: table.filters.from ? startOfDayParam(table.filters.from) : undefined,
      to: table.filters.to ? endOfDayParam(table.filters.to) : undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const refundsQuery = useQuery({
    queryKey: queryKeys.refunds.list(filters),
    queryFn: () => fetchRefunds(filters),
    placeholderData: (previous) => previous,
  })

  // The summary is computed over the same filters, server-side, so the totals
  // above the table always describe the rows in it.
  const summaryQuery = useQuery({
    queryKey: queryKeys.refunds.summary(filters),
    queryFn: () => fetchRefundSummary(filters),
    placeholderData: (previous) => previous,
  })

  const adminsQuery = useQuery({
    queryKey: queryKeys.refunds.admins,
    queryFn: fetchRefundAdmins,
    staleTime: 5 * 60_000,
  })

  // The drawer holds a snapshot; after a re-check the list is refetched, so the
  // open record is re-read from the fresh page rather than left stale.
  useEffect(() => {
    if (!selected || !refundsQuery.data) return
    const refreshed = refundsQuery.data.content.find((refund) => refund.id === selected.id)
    if (refreshed && refreshed !== selected) {
      setSelected(refreshed)
    }
  }, [refundsQuery.data, selected])

  const columns = useMemo<ColumnDef<AdminRefund, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'Refund',
        meta: { sortKey: 'id', mobileLabel: 'Refund' },
        cell: ({ row }) => (
          <StackedCell
            primary={<span className={styles.mono}>#{row.original.id}</span>}
            secondary={formatDateTimeCompact(row.original.createdAt)}
          />
        ),
      },
      {
        id: 'bookingReference',
        header: 'Booking',
        meta: { sortKey: 'bookingReference', mobileLabel: 'Booking' },
        cell: ({ row }) =>
          row.original.bookingReference ? (
            <StackedCell
              primary={<span className={styles.mono}>{row.original.bookingReference}</span>}
              secondary={row.original.tripRequestId ? `Request #${row.original.tripRequestId}` : undefined}
            />
          ) : (
            <span className={styles.muted}>Card verification</span>
          ),
      },
      {
        id: 'customerName',
        header: 'Customer',
        meta: { sortKey: 'customerName', mobileLabel: 'Customer' },
        cell: ({ row }) => (
          <StackedCell
            primary={row.original.customerName ?? '—'}
            secondary={row.original.customerUserId ? `User #${row.original.customerUserId}` : undefined}
          />
        ),
      },
      {
        id: 'paymobTransactionId',
        header: 'Paymob transaction',
        meta: { sortKey: 'paymobTransactionId', mobileLabel: 'Paymob', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={<span className={styles.mono}>{row.original.paymobTransactionId ?? '—'}</span>}
            secondary={
              row.original.paymobRefundTransactionId
                ? `refund ${row.original.paymobRefundTransactionId}`
                : 'no refund txn'
            }
          />
        ),
      },
      {
        id: 'requestedAmountMinor',
        header: 'Amount',
        meta: { sortKey: 'requestedAmountMinor', mobileLabel: 'Amount', align: 'right' },
        cell: ({ row }) => (
          <StackedCell
            primary={
              <strong>
                {formatMinorCurrency(
                  row.original.refundedAmountMinor ?? row.original.requestedAmountMinor,
                  row.original.currency,
                )}
              </strong>
            }
            secondary={`of ${formatMinorCurrency(row.original.originalAmountMinor, row.original.currency)}`}
          />
        ),
      },
      {
        id: 'source',
        header: 'Source',
        meta: { sortKey: 'source', mobileLabel: 'Source', hideOnMobile: true },
        cell: ({ row }) => <span className={styles.chip}>{humanizeEnum(row.original.source)}</span>,
      },
      {
        id: 'reason',
        header: 'Reason',
        meta: { mobileLabel: 'Reason', width: '200px' },
        cell: ({ row }) => (
          <span className={styles.reason} title={row.original.reason}>
            {truncate(row.original.reason, 70)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sortKey: 'status', mobileLabel: 'Status' },
        cell: ({ row }) => (
          <div className={styles.statusCell}>
            <StatusBadge status={row.original.status} />
            {row.original.needsAttention ? (
              <span className={styles.attentionFlag} title="Needs an operator's attention">
                <BadgeAlert aria-hidden="true" />
                <span className="visually-hidden">Needs attention</span>
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'adminName',
        header: 'Requested by',
        meta: { sortKey: 'adminName', mobileLabel: 'Requested by', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={row.original.adminName ?? 'System'}
            secondary={row.original.completedAt ? formatDateTimeCompact(row.original.completedAt) : 'not settled'}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '130px' },
        cell: ({ row }) => (
          <RowActions>
            <Button
              size="sm"
              variant="secondary"
              iconRight={<ArrowRight aria-hidden="true" />}
              onClick={() => setSelected(row.original)}
            >
              Details
            </Button>
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminRefund>[]>(
    () => [
      { header: 'Refund ID', value: (row) => row.id },
      { header: 'Created', value: (row) => formatDateTimeCompact(row.createdAt, '') },
      { header: 'Completed', value: (row) => formatDateTimeCompact(row.completedAt, '') },
      { header: 'Status', value: (row) => row.status ?? '' },
      { header: 'Source', value: (row) => row.source ?? '' },
      { header: 'Booking reference', value: (row) => row.bookingReference ?? '' },
      { header: 'Trip request ID', value: (row) => row.tripRequestId ?? '' },
      { header: 'Trip ID', value: (row) => row.tripId ?? '' },
      { header: 'Customer', value: (row) => row.customerName ?? '' },
      { header: 'Customer ID', value: (row) => row.customerUserId ?? '' },
      { header: 'Paymob transaction', value: (row) => row.paymobTransactionId ?? '' },
      { header: 'Paymob refund transaction', value: (row) => row.paymobRefundTransactionId ?? '' },
      { header: 'Paymob order', value: (row) => row.paymobOrderId ?? '' },
      { header: 'Currency', value: (row) => row.currency },
      // Exported in major units so a finance spreadsheet can sum them directly.
      { header: 'Original amount', value: (row) => (row.originalAmountMinor / 100).toFixed(2) },
      { header: 'Requested amount', value: (row) => (row.requestedAmountMinor / 100).toFixed(2) },
      {
        header: 'Refunded amount',
        value: (row) => (row.refundedAmountMinor === null ? '' : (row.refundedAmountMinor / 100).toFixed(2)),
      },
      { header: 'Reason', value: (row) => row.reason },
      { header: 'Internal note', value: (row) => row.adminNote ?? '' },
      { header: 'Requested by', value: (row) => row.adminName ?? 'System' },
      { header: 'Requested by email', value: (row) => row.adminEmail ?? '' },
      { header: 'Gateway response', value: (row) => row.gatewayResponseCode ?? '' },
      { header: 'Gateway message', value: (row) => row.gatewayMessage ?? '' },
      { header: 'Attempts', value: (row) => row.attemptCount },
    ],
    [],
  )

  const page = refundsQuery.data

  return (
    <>
      <PageHeader
        title="Refunds"
        description="Every refund the platform has attempted through Paymob — successful, rejected, and the ones whose outcome is still unknown. These records are permanent: nothing here can be edited or removed."
        meta={
          page ? (
            <>
              <span>{page.totalElements} matching refunds</span>
              {summaryQuery.data && summaryQuery.data.needsAttentionCount > 0 ? (
                <span className={styles.metaAlert}>
                  {summaryQuery.data.needsAttentionCount} need attention
                </span>
              ) : null}
            </>
          ) : null
        }
      />

      <RefundSummaryCards
        summary={summaryQuery.data}
        isLoading={summaryQuery.isLoading}
        isError={summaryQuery.isError}
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search booking reference, customer, reason or a Paymob transaction ID…"
              label="Search refunds"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-refunds"
              title="Drive For Me — Refunds"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchRefunds({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Status"
            value={table.filters.status}
            onChange={(value) => table.setFilter('status', value)}
            options={REFUND_STATUSES.map((status) => ({ value: status, label: humanizeEnum(status) }))}
          />
          <FilterSelect
            label="Source"
            value={table.filters.source}
            onChange={(value) => table.setFilter('source', value)}
            options={REFUND_SOURCES.map((source) => ({ value: source, label: humanizeEnum(source) }))}
          />
          <FilterSelect
            label="Requested by"
            value={table.filters.adminUserId}
            onChange={(value) => table.setFilter('adminUserId', value)}
            allLabel="Anyone"
            options={(adminsQuery.data ?? []).map((admin) => ({
              value: String(admin.id),
              label: admin.name ?? admin.email ?? `Admin #${admin.id}`,
            }))}
          />
          <FilterDate
            label="From"
            value={table.filters.from}
            onChange={(value) => table.setFilter('from', value)}
            max={table.filters.to || undefined}
          />
          <FilterDate
            label="To"
            value={table.filters.to}
            onChange={(value) => table.setFilter('to', value)}
            min={table.filters.from || undefined}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me refund ledger"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={refundsQuery.isLoading}
          isFetching={refundsQuery.isFetching && !refundsQuery.isLoading}
          error={refundsQuery.isError ? refundsQuery.error : undefined}
          onRetry={() => void refundsQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No refunds match these filters' : 'No refunds yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try a different status or widen the date range.'
              : 'Refunds appear here as soon as one is attempted — from this dashboard, from a cancellation, or automatically after a card is saved.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="refund"
            disabled={refundsQuery.isFetching}
          />
        ) : null}
      </Card>

      <RefundDetailsDrawer refund={selected} onClose={() => setSelected(null)} />
    </>
  )
}
